/**
 * Form Filling Gate - v2 with Mode Selection
 * 
 * Password-protected entry point for form filling.
 * After password verification, users choose between AI-assisted or manual form filling.
 */

import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';

const FormFillingPage = lazy(() => import('./FormFillingPage'));
const ManualFormFillingPage = lazy(() => import('./ManualFormFillingPage'));

const API_BASE = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  50% { transform: translateX(10px); }
  75% { transform: translateX(-5px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0c0f1a 0%, #1a1f35 50%, #0f1628 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const GateCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 24px;
  padding: 48px 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(99, 102, 241, 0.1);
  animation: ${fadeIn} 0.5s ease-out;
  text-align: center;
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  box-shadow: 0 12px 40px rgba(99, 102, 241, 0.4);
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const FormName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 32px;
  padding: 12px 20px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
`;

const PasswordLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: rgba(148, 163, 184, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  text-align: left;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;
  ${props => props.$error && css`animation: ${shake} 0.5s ease;`}
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 18px 20px;
  font-size: 24px;
  font-family: monospace;
  letter-spacing: 8px;
  text-align: center;
  text-transform: uppercase;
  background: rgba(15, 23, 42, 0.8);
  border: 2px solid ${props => props.$error ? '#ef4444' : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 16px;
  color: #f1f5f9;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    border-color: ${props => props.$error ? '#ef4444' : '#6366f1'};
    box-shadow: 0 0 0 4px ${props => props.$error ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.15)'};
  }
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.3);
    letter-spacing: 4px;
  }
`;

const ErrorMessage = styled.div`
  color: #f87171;
  font-size: 13px;
  margin-top: 8px;
  text-align: center;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(99, 102, 241, 0.5);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.5);
  margin: 24px 0 0 0;
  line-height: 1.5;
`;

const StatusScreen = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const StatusIcon = styled.div`
  font-size: 56px;
  margin-bottom: 24px;
  ${props => props.$animate && css`animation: ${pulse} 2s ease-in-out infinite;`}
`;

const StatusTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 12px 0;
`;

const StatusText = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.7);
  margin: 0;
  line-height: 1.5;
`;

// ============================================
// MODE SELECTION STYLED COMPONENTS
// ============================================

const ModeSelectionWrapper = styled.div`
  width: 100%;
  max-width: 680px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ModeHeader = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

const ModeTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 10px 0;
`;

const ModeSubtitle = styled.p`
  font-size: 15px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const ModeFormBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 20px;
  color: #a5b4fc;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const ModeCardsRow = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const ModeCard = styled.button`
  flex: 1;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%);
  border: 2px solid ${props => props.$highlighted ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 20px;
  padding: 32px 28px;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.6);
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(99, 102, 241, 0.15);
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

const ModeCardGlow = styled.div`
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at center, ${props => props.$color || 'rgba(99, 102, 241, 0.05)'} 0%, transparent 70%);
  pointer-events: none;
`;

const ModeCardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 20px;
  background: ${props => props.$bg || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  box-shadow: 0 8px 24px ${props => props.$shadow || 'rgba(99, 102, 241, 0.3)'};
`;

const ModeCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
`;

const ModeCardDescription = styled.p`
  font-size: 13px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const ModeFeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ModeFeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(196, 181, 253, 0.9);
  margin-bottom: 8px;
  
  &:last-child { margin-bottom: 0; }
`;

const ModeFeatureDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$color || '#a5b4fc'};
  flex-shrink: 0;
`;

const ModeRecommendedBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ============================================
// COMPONENT
// ============================================

const FormFillingGate = () => {
  const { sessionId } = useParams();
  
  const [status, setStatus] = useState('loading'); // loading | password | mode-select | ai-mode | manual-mode | error | expired
  const [session, setSession] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  
  const inputRef = useRef(null);

  console.log('[FormFillingGate] Current status:', status);
  
  // Check if user already has access
  useEffect(() => {
    const checkAccess = async () => {
      try {
        console.log('[FormFillingGate] Checking access for session:', sessionId);
        const response = await axios.get(`${API_BASE}/form-filling/sessions/${sessionId}/check-access`);
        console.log('[FormFillingGate] check-access response:', response.data);
        
        if (response.data.success) {
          if (response.data.hasAccess) {
            console.log('[FormFillingGate] Has access -> mode-select');
            setSession(response.data.session);
            setStatus('mode-select');
          } else {
            console.log('[FormFillingGate] No access -> password');
            setSession(response.data.session);
            setStatus('password');
          }
        }
      } catch (err) {
        console.error('Error checking access:', err);
        if (err.response?.status === 410) {
          setStatus('expired');
        } else if (err.response?.status === 404) {
          setError('Session not found');
          setStatus('error');
        } else {
          setError('Failed to load session');
          setStatus('error');
        }
      }
    };
    
    if (sessionId) {
      checkAccess();
    }
  }, [sessionId]);
  
  // Auto-focus input when showing password screen
  useEffect(() => {
    if (status === 'password' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);
  
  // Handle password submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!password.trim() || password.length < 6) {
      setError('Please enter the 6-character password from your email');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }
    
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_BASE}/form-filling/sessions/${sessionId}/verify-password`, {
        password: password.trim()
      });
      
      if (response.data.success) {
        console.log('[FormFillingGate] Password verified -> mode-select');
        setSession(response.data.session);
        setStatus('mode-select');
      } else {
        setError(response.data.error || 'Invalid password');
        setShakeError(true);
        setTimeout(() => setShakeError(false), 500);
      }
    } catch (err) {
      console.error('Error verifying password:', err);
      if (err.response?.status === 401) {
        setError('Incorrect password. Please check your email and try again.');
      } else if (err.response?.status === 410) {
        setStatus('expired');
      } else {
        setError('Failed to verify password. Please try again.');
      }
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    } finally {
      setVerifying(false);
    }
  };
  
  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };
  
  // Loading state
  if (status === 'loading') {
    return (
      <PageWrapper>
        <GateCard>
          <StatusScreen>
            <StatusIcon $animate>📋</StatusIcon>
            <StatusTitle>Loading...</StatusTitle>
            <StatusText>Please wait while we prepare your form</StatusText>
          </StatusScreen>
        </GateCard>
      </PageWrapper>
    );
  }
  
  // Expired state
  if (status === 'expired') {
    return (
      <PageWrapper>
        <GateCard>
          <StatusScreen>
            <StatusIcon>⏰</StatusIcon>
            <StatusTitle>Link Expired</StatusTitle>
            <StatusText>
              This form filling link has expired.<br />
              Please contact your advisor to get a new link.
            </StatusText>
          </StatusScreen>
        </GateCard>
      </PageWrapper>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <PageWrapper>
        <GateCard>
          <StatusScreen>
            <StatusIcon>❌</StatusIcon>
            <StatusTitle>Something Went Wrong</StatusTitle>
            <StatusText>{error || 'Unable to load the form. Please try again later.'}</StatusText>
          </StatusScreen>
        </GateCard>
      </PageWrapper>
    );
  }
  
  // Mode selection screen
  if (status === 'mode-select') {
    return (
      <PageWrapper>
        <ModeSelectionWrapper>
          <ModeHeader>
            <LogoIcon>📋</LogoIcon>
            <ModeFormBadge>
              {session?.formName || 'Form'}
            </ModeFormBadge>
            <ModeTitle>How would you like to fill this form?</ModeTitle>
            <ModeSubtitle>
              Choose the method that works best for you. Both options will save your answers to the same form.
            </ModeSubtitle>
          </ModeHeader>

          <ModeCardsRow>
            {/* AI Mode */}
            <ModeCard $highlighted onClick={() => setStatus('ai-mode')}>
              <ModeRecommendedBadge>Recommended</ModeRecommendedBadge>
              <ModeCardGlow $color="rgba(99, 102, 241, 0.08)" />
              <ModeCardIcon $bg="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" $shadow="rgba(99, 102, 241, 0.3)">
                🤖
              </ModeCardIcon>
              <ModeCardTitle>AI-Assisted</ModeCardTitle>
              <ModeCardDescription>
                Chat naturally and let our AI guide you through the form. Just answer in your own words.
              </ModeCardDescription>
              <ModeFeatureList>
                <ModeFeatureItem><ModeFeatureDot $color="#818cf8" />Conversational chat interface</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#818cf8" />AI extracts answers automatically</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#818cf8" />Smart suggestions and guidance</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#818cf8" />Answer in any language</ModeFeatureItem>
              </ModeFeatureList>
            </ModeCard>

            {/* Manual Mode */}
            <ModeCard onClick={() => setStatus('manual-mode')}>
              <ModeCardGlow $color="rgba(16, 185, 129, 0.06)" />
              <ModeCardIcon $bg="linear-gradient(135deg, #10b981 0%, #059669 100%)" $shadow="rgba(16, 185, 129, 0.3)">
                ✏️
              </ModeCardIcon>
              <ModeCardTitle>Fill Manually</ModeCardTitle>
              <ModeCardDescription>
                Traditional form with dropdowns, checkboxes, and text fields. Select from predefined options.
              </ModeCardDescription>
              <ModeFeatureList>
                <ModeFeatureItem><ModeFeatureDot $color="#34d399" />Structured form layout</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#34d399" />Dropdowns and checkboxes</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#34d399" />Reduced input errors</ModeFeatureItem>
                <ModeFeatureItem><ModeFeatureDot $color="#34d399" />Section-by-section progress</ModeFeatureItem>
              </ModeFeatureList>
            </ModeCard>
          </ModeCardsRow>
        </ModeSelectionWrapper>
      </PageWrapper>
    );
  }
  
  // AI mode - show the conversational form
  if (status === 'ai-mode') {
    return (
      <Suspense fallback={
        <PageWrapper>
          <GateCard>
            <StatusScreen>
              <StatusIcon $animate>📋</StatusIcon>
              <StatusTitle>Loading Form...</StatusTitle>
              <StatusText>Please wait while we prepare your form</StatusText>
            </StatusScreen>
          </GateCard>
        </PageWrapper>
      }>
        <FormFillingPage />
      </Suspense>
    );
  }
  
  // Manual mode - show the structured form
  if (status === 'manual-mode') {
    return (
      <Suspense fallback={
        <PageWrapper>
          <GateCard>
            <StatusScreen>
              <StatusIcon $animate>📋</StatusIcon>
              <StatusTitle>Loading Form...</StatusTitle>
              <StatusText>Please wait while we prepare your form</StatusText>
            </StatusScreen>
          </GateCard>
        </PageWrapper>
      }>
        <ManualFormFillingPage />
      </Suspense>
    );
  }
  
  // Password entry screen
  return (
    <PageWrapper>
      <GateCard>
        <LogoIcon>🔐</LogoIcon>
        <Title>Enter Your Password</Title>
        <Subtitle>
          Please enter the 6-character password from the email you received
        </Subtitle>
        
        {session?.userName && (
          <FormName>
            Welcome, {session.userName}!
          </FormName>
        )}
        
        <form onSubmit={handleSubmit}>
          <PasswordLabel>Access Password</PasswordLabel>
          <PasswordInputWrapper $error={shakeError}>
            <PasswordInput
              ref={inputRef}
              type="text"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value.toUpperCase().slice(0, 6));
                setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="• • • • • •"
              maxLength={6}
              autoComplete="off"
              $error={!!error}
              disabled={verifying}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </PasswordInputWrapper>
          
          <SubmitButton type="submit" disabled={verifying || password.length < 6}>
            {verifying ? (
              <>
                <Spinner />
                Verifying...
              </>
            ) : (
              <>
                🔓 Access Form
              </>
            )}
          </SubmitButton>
        </form>
        
        <HelpText>
          Can't find your password? Check your email inbox for a message from iMaven with the subject "Action Required: Please Complete Your Form"
        </HelpText>
      </GateCard>
    </PageWrapper>
  );
};

export default FormFillingGate;
